import { useEffect, useCallback } from 'react';
import { Play } from 'lucide-react';
import type { ProjectTask } from '@/types/task';
import { useMediaPlayer } from '../hooks/useMediaPlayer';
import { useContainerDimensions } from '../hooks/useContainerDimensions';
import { MediaControls } from './MediaControls';
import { AnnotationLayer, type NewTaskClickData } from './AnnotationLayer';

interface VideoPlayerProps {
  resource: {
    id: string;
    signedUrl?: string;
    mimeType?: string;
    [k: string]: unknown;
  };
  tasks: ProjectTask[];
  selectedTask?: ProjectTask | null;
  updateSelectedTask: (task: ProjectTask | null) => void;
  /** Whether the tasks tab is active (vs messages) */
  messagesTabOpen: boolean;
  highestIndex: number;
  userRole: string | null;
  /** Called when user clicks/draws to create a new task */
  onCreateTask?: (data: NewTaskClickData) => void;
  /** Exposes the video element ref (for snapshot capture) */
  videoRefCallback?: (el: HTMLVideoElement | null) => void;
}

export function VideoPlayer({
  resource,
  tasks,
  selectedTask,
  updateSelectedTask,
  messagesTabOpen,
  highestIndex,
  userRole,
  onCreateTask,
  videoRefCallback,
}: VideoPlayerProps) {
  const player = useMediaPlayer();
  const { ref: dimRef, dimensions } = useContainerDimensions();

  const isViewer = userRole === 'viewer' || userRole === 'guest';
  const tasksVisible = !messagesTabOpen;

  // Reload media when resource changes
  useEffect(() => {
    player.load();
  }, [resource.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seek to selected task timestamp
  useEffect(() => {
    if (selectedTask?.timestamp != null) {
      const timeSec = Number(selectedTask.timestamp) / 1000;
      player.seek(timeSec);
      player.pause();
    }
  }, [selectedTask?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selected task on seek away
  useEffect(() => {
    if (!selectedTask || messagesTabOpen) return;

    const el = player.ref.current;
    if (!el) return;

    const onSeeked = () => {
      if (!selectedTask?.timestamp) return;
      const taskTime = Math.round(Number(selectedTask.timestamp) / 100);
      const currentDeciSec = Math.round(el.currentTime * 10);
      if (taskTime !== currentDeciSec) {
        updateSelectedTask(null);
      }
    };

    el.addEventListener('seeked', onSeeked);
    return () => el.removeEventListener('seeked', onSeeked);
  }, [selectedTask, messagesTabOpen, player.ref, updateSelectedTask]);

  // Handle create-task with timestamp
  const handleCreateTask = useCallback(
    (data: NewTaskClickData) => {
      player.pause();
      onCreateTask?.({
        ...data,
        time: (player.ref.current?.currentTime ?? 0) * 1000,
      });
    },
    [player, onCreateTask],
  );

  // Fix quicktime mime type
  const mimeType = resource.mimeType === 'video/quicktime' ? 'video/mp4' : resource.mimeType;

  return (
    <div
      ref={(el) => {
        // Share ref between containerRef (for fullscreen) and dimRef (for dimensions)
        player.containerRef.current = el;
        (dimRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="group/player relative size-full bg-black rounded-lg overflow-hidden"
    >
      {/* Native video element */}
      <video
        ref={(el) => {
          (player.ref as React.MutableRefObject<HTMLVideoElement | null>).current = el;
          videoRefCallback?.(el);
        }}
        className="size-full object-contain"
        preload="auto"
        crossOrigin="anonymous"
        onClick={player.togglePlay}
      >
        {resource.signedUrl && <source src={resource.signedUrl} type={mimeType} />}
      </video>

      {/* Play icon overlay — visible when paused and not hovered */}
      {!player.isPlaying && (
        <div
          className="absolute inset-0 z-1 flex items-center justify-center opacity-80 group-hover/player:opacity-0 transition-opacity pointer-events-none"
        >
          <div className="flex size-16 items-center justify-center rounded-full bg-black/50">
            <Play className="size-8 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Annotation layer (dots, arrows, drawing) */}
      <AnnotationLayer
        containerRef={dimRef}
        dimensions={dimensions}
        tasks={tasks}
        selectedTask={selectedTask}
        onSelectTask={updateSelectedTask}
        onCreateTask={!isViewer ? handleCreateTask : undefined}
        currentTime={player.currentTime}
        canCreate={!isViewer && tasksVisible}
        mediaType="video"
        tasksVisible={tasksVisible}
      />

      {/* Controls overlay */}
      <MediaControls
        currentTime={player.currentTime}
        duration={player.duration}
        isPlaying={player.isPlaying}
        isFullscreen={player.isFullscreen}
        playbackRate={player.playbackRate}
        onTogglePlay={player.togglePlay}
        onSeek={player.seek}
        onSetRate={player.setRate}
        onToggleFullscreen={player.toggleFullscreen}
        tasks={tasksVisible ? tasks : undefined}
        selectedTaskId={selectedTask?.id}
        onSelectTask={(t) => {
          player.seek(Number(t.timestamp ?? 0) / 1000);
          player.pause();
          updateSelectedTask(t as ProjectTask);
        }}
        mediaRef={player.ref}
      />
    </div>
  );
}
